import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type {
  AccesEspaceReponse,
  ContactEspaceReponse,
  PropositionEspaceReponse,
  ConseilConservationReponse,
  ReglagesEspaceReponse,
  partageEspaceSchema,
} from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
import type { Pagination } from './types.js';

export type EtatAcces = AccesEspaceReponse['etat'];
export type GesteAcces = 'renvoyer' | 'suspendre' | 'reactiver' | 'revoquer';
export interface OeuvreEspace {
  conseil: ConseilConservationReponse;
  contacts: { id: string; nom: string; etat: EtatAcces | null }[];
}
export type ReglagesAvecHote = ReglagesEspaceReponse & { hote: string };
const racine = (g: string) => `/g/${encodeURIComponent(g)}/espace-collectionneur`;
const contact = (g: string, id: string) => `${racine(g)}/contacts/${encodeURIComponent(id)}`;
const oeuvre = (g: string, id: string) => `${racine(g)}/oeuvres/${encodeURIComponent(id)}`;
export const espaceCollectionneurApi = {
  acces: (g: string, etat: EtatAcces | '', page: number, appel?: AppelApi) =>
    api.get<{
      acces: (AccesEspaceReponse & { contactNom: string; elementsPartages: number })[];
      pagination: Pagination;
    }>(
      `${racine(g)}/acces?${new URLSearchParams({ ...(etat ? { etat } : {}), page: String(page), parPage: '50' })}`,
      { galerie: g, ...appel },
    ),
  contact: (g: string, id: string, appel?: AppelApi) =>
    api.get<ContactEspaceReponse>(contact(g, id), { galerie: g, ...appel }),
  propositions: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ propositions: PropositionEspaceReponse[] }>(`${contact(g, id)}/propositions`, {
      galerie: g,
      ...appel,
    }),
  inviter: (g: string, id: string, appel?: AppelApi) =>
    api.post<{ acces: AccesEspaceReponse }>(
      `${contact(g, id)}/invitation`,
      {},
      { galerie: g, ...appel },
    ),
  geste: (
    g: string,
    id: string,
    geste: GesteAcces,
    corps: { version: number; motif?: string },
    appel?: AppelApi,
  ) =>
    api.post<{ acces: AccesEspaceReponse }>(
      `${racine(g)}/acces/${encodeURIComponent(id)}/${geste}`,
      corps,
      { galerie: g, ...appel },
    ),
  partager: (g: string, id: string, corps: z.input<typeof partageEspaceSchema>, appel?: AppelApi) =>
    api.post(`${contact(g, id)}/partages`, corps, { galerie: g, ...appel }),
  retirer: (g: string, id: string, appel?: AppelApi) =>
    api.delete<void>(`${racine(g)}/partages/${encodeURIComponent(id)}`, { galerie: g, ...appel }),
  oeuvre: (g: string, id: string, appel?: AppelApi) =>
    api.get<OeuvreEspace>(oeuvre(g, id), { galerie: g, ...appel }),
  conserver: (g: string, id: string, corps: ConseilConservationReponse, appel?: AppelApi) =>
    api.put<ConseilConservationReponse>(`${oeuvre(g, id)}/conservation`, corps, {
      galerie: g,
      ...appel,
    }),
  reglages: (g: string, appel?: AppelApi) =>
    api.get<ReglagesAvecHote>(`${racine(g)}/reglages`, { galerie: g, ...appel }),
  regler: (g: string, corps: ReglagesEspaceReponse, appel?: AppelApi) =>
    api.put<ReglagesEspaceReponse>(`${racine(g)}/reglages`, corps, { galerie: g, ...appel }),
};
export function useContactEspace(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'espace-collectionneur', 'contact', id),
    queryFn: (c) => espaceCollectionneurApi.contact(g, id, appelDeRequete(c)),
  });
}
export function usePropositionsEspace(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'espace-collectionneur', 'propositions', id),
    queryFn: (c) => espaceCollectionneurApi.propositions(g, id, appelDeRequete(c)),
  });
}
export function useOeuvreEspace(g: string, id: string | null) {
  return useQuery({
    queryKey: cleMetier(g, 'espace-collectionneur', 'oeuvre', id),
    queryFn: (c) => espaceCollectionneurApi.oeuvre(g, id!, appelDeRequete(c)),
    enabled: id !== null,
  });
}
export function useAccesEspaces(g: string, etat: EtatAcces | '', page: number) {
  return useQuery({
    queryKey: cleMetier(g, 'espace-collectionneur', 'acces', etat, page),
    queryFn: (c) => espaceCollectionneurApi.acces(g, etat, page, appelDeRequete(c)),
  });
}
export function useReglagesEspace(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'espace-collectionneur', 'reglages'),
    queryFn: (c) => espaceCollectionneurApi.reglages(g, appelDeRequete(c)),
  });
}
export function useActionEspace<T, V>(
  g: string,
  action: (corps: V, appel: AppelApi) => Promise<T>,
) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: action,
      onSuccess: () =>
        client.invalidateQueries({ queryKey: cleMetier(g, 'espace-collectionneur') }),
    }),
  );
}
